import { supabase } from '../lib/supabase';
import { isDemoMode, getDemoOrgId, getAuthOrgId } from '../utils/demoHelper';

// Helper function to convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (!obj) return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  if (typeof obj !== 'object') return obj;

  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

// Helper function to convert camelCase to snake_case
const toSnakeCase = (obj) => {
  if (!obj) return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);
  if (typeof obj !== 'object') return obj;

  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, (letter) => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj?.[key]);
    return acc;
  }, {});
};

// Check if error is schema-related
function isSchemaError(error) {
  if (!error) return false;
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') return true;
    if (errorClass === '23') return false;
  }
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  return false;
}

export const permitService = {
  // Get all permits with project info
  async getAll() {
    try {
      let query = supabase
        ?.from('permits')
        ?.select('*, projects(id, customer_name, address)')
        ?.order('created_at', { ascending: false });

      // Demo mode: use stored demo org_id directly
      if (isDemoMode()) {
        const demoOrgId = getDemoOrgId();
        if (demoOrgId) {
          query = query?.eq('organization_id', demoOrgId);
        }
      } else {
        // Use cached org_id from AuthContext (never stale after token refresh)
        const orgId = getAuthOrgId();
        if (orgId) {
          query = query?.eq('organization_id', orgId);
        }
      }

      const { data, error } = await query;

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error:', error?.message);
          return [];
        }
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching permits:', error);
      throw error;
    }
  },

  // Get permit by ID
  async getById(id) {
    try {
      const { data, error } = await supabase
        ?.from('permits')
        ?.select('*')
        ?.eq('id', id)
        ?.maybeSingle();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          return null;
        }
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Error fetching permit:', error);
      throw error;
    }
  },

  // Get permit with project details
  async getByIdWithProject(id) {
    try {
      const { data, error } = await supabase
        ?.from('permits')
        ?.select('*, projects(id, customer_name, address, phase, customer_id, customers(name))')
        ?.eq('id', id)
        ?.maybeSingle();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          return null;
        }
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Error fetching permit with project:', error);
      throw error;
    }
  },

  // Get permits by project
  async getByProject(projectId) {
    try {
      const { data, error } = await supabase
        ?.from('permits')
        ?.select('*')
        ?.eq('project_id', projectId)
        ?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching permits by project:', error);
      throw error;
    }
  },

  // Create new permit
  async create(permitData) {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user) throw new Error('User not authenticated');
      const user = session?.user;

      // Use cached org_id from AuthContext
      const organizationId = getAuthOrgId();
      if (!organizationId) throw new Error('Unable to determine user organization');

      const snakeData = toSnakeCase({
        ...permitData,
        createdBy: user?.id,
        organizationId
      });

      const { data, error } = await supabase
        ?.from('permits')
        ?.insert(snakeData)
        ?.select('*, projects(id, customer_name, address)')
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          throw new Error(error.message);
        }
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Error creating permit:', error);
      throw error;
    }
  },

  // Update permit
  async update(id, permitData) {
    try {
      const snakeData = toSnakeCase(permitData);

      const { data, error } = await supabase
        ?.from('permits')
        ?.update(snakeData)
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          throw new Error(error.message);
        }
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Error updating permit:', error);
      throw error;
    }
  },

  // Update permit status only
  async updateStatus(id, status) {
    try {
      const { data, error } = await supabase
        ?.from('permits')
        ?.update({ status, updated_at: new Date()?.toISOString() })
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        throw new Error(error.message);
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Error updating permit status:', error);
      throw error;
    }
  },

  // Delete permit
  async delete(id) {
    try {
      const { error } = await supabase?.from('permits')?.delete()?.eq('id', id);

      if (error) {
        if (isSchemaError(error)) throw error;
        throw new Error(error.message);
      }

      return true;
    } catch (error) {
      console.error('Error deleting permit:', error);
      throw error;
    }
  },

  // Upload document to permit-documents bucket
  async uploadDocument(permitId, file) {
    try {
      const fileName = `${permitId}/${Date.now()}-${file?.name}`;

      const { data, error } = await supabase?.storage
        ?.from('permit-documents')
        ?.upload(fileName, file, { cacheControl: '3600', upsert: false });

      if (error) throw new Error(error.message);

      return { path: data?.path, name: file?.name, size: file?.size };
    } catch (error) {
      console.error('Error uploading document:', error);
      throw error;
    }
  },

  // List documents for a permit
  async listDocuments(permitId) {
    try {
      const { data, error } = await supabase?.storage
        ?.from('permit-documents')
        ?.list(permitId, { limit: 100, sortBy: { column: 'created_at', order: 'desc' } });

      if (error) {
        console.log('Error listing documents:', error?.message);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error listing documents:', error);
      return [];
    }
  },

  // Get signed URL for a document
  async getDocumentUrl(filePath) {
    try {
      const { data, error } = await supabase?.storage
        ?.from('permit-documents')
        ?.createSignedUrl(filePath, 3600);

      if (error) {
        console.error('Error creating signed URL:', error?.message, 'for path:', filePath);
        throw new Error(`Unable to generate document URL: ${error.message}`);
      }

      if (!data?.signedUrl) {
        throw new Error('No signed URL returned from storage');
      }

      return data?.signedUrl;
    } catch (error) {
      console.error('Error getting document URL:', error);
      throw error;
    }
  },

  // Delete a document
  async deleteDocument(filePath) {
    try {
      const { error } = await supabase?.storage
        ?.from('permit-documents')
        ?.remove([filePath]);

      if (error) throw new Error(error.message);

      return true;
    } catch (error) {
      console.error('Error deleting document:', error);
      throw error;
    }
  }
};